import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import { CANVAS_WIDTH, CANVAS_HEIGHT, COLORS, GROUND_Y } from '@/shared/constants';
import type { HitBox, FighterState, AttackType } from '@/shared/types';
import { FighterDisplay, type FighterDisplayState } from './fighter-display';
import { HitEffectManager } from './hit-effects';
import { getGameConfig } from './game-config';
import { logger } from './logger';

/**
 * PixiJS-based renderer replacing the old Canvas 2D renderer.
 * Manages the PixiJS Application and all display objects.
 * Uses object pools for Text and Graphics to prevent GPU memory leaks on mobile.
 */
export class Renderer {
  private app: Application;
  private initialized = false;

  // Layers (ordered back to front)
  private bgLayer: Container;
  private groundLayer: Container;
  private fighterLayer: Container;
  private effectsLayer: Container;
  private uiLayer: Container;
  private overlayLayer: Container;

  /** Particle hit effects manager — lives on the effects layer above fighters. */
  readonly hitEffects: HitEffectManager;

  // Cached display objects

  // Fighter displays
  private fighterDisplays: Map<string, FighterDisplay> = new Map();

  // Object pools to prevent GPU memory leaks (mobile crash fix)
  private textPool: Text[] = [];
  private textPoolIndex = 0;
  private graphicsPool: Graphics[] = [];
  private graphicsPoolIndex = 0;

  // Pre-allocated TextStyle cache to avoid re-creating styles each frame
  private styleCache: Map<string, TextStyle> = new Map();

  constructor() {
    this.app = new Application();
    this.bgLayer = new Container();
    this.groundLayer = new Container();
    this.fighterLayer = new Container();
    this.effectsLayer = new Container();
    this.uiLayer = new Container();
    this.overlayLayer = new Container();
    this.hitEffects = new HitEffectManager();
  }

  /**
   * Initialize the PixiJS application. Must be called before any rendering.
   */
  async init(canvasContainer: HTMLElement): Promise<HTMLCanvasElement> {
    await this.app.init({
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      backgroundColor: COLORS.bg,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    // Add layers to stage (ordered back to front)
    this.app.stage.addChild(this.bgLayer);
    this.app.stage.addChild(this.groundLayer);
    this.app.stage.addChild(this.fighterLayer);
    this.app.stage.addChild(this.effectsLayer);

    // UI + overlay sit on top
    this.app.stage.addChild(this.uiLayer);
    this.app.stage.addChild(this.overlayLayer);

    // Mount hit-effect container onto the effects layer
    this.effectsLayer.addChild(this.hitEffects.container);

    // Build static background
    this.buildBackground();
    this.buildGround();

    this.initialized = true;

    // Append canvas to container
    canvasContainer.appendChild(this.app.canvas);

    return this.app.canvas as HTMLCanvasElement;
  }

  get canvas(): HTMLCanvasElement {
    return this.app.canvas as HTMLCanvasElement;
  }

  private buildBackground(): void {
    const bg = new Graphics();

    // Sky gradient (approximated with stripes)
    const bgColor = parseInt(COLORS.bg.replace('#', ''), 16);
    bg.rect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    bg.fill({ color: bgColor });

    // Atmospheric layers
    for (let i = 0; i < 8; i++) {
      const y = i * (GROUND_Y / 8);
      const alpha = 0.03 + i * 0.01;
      bg.rect(0, y, CANVAS_WIDTH, GROUND_Y / 8);
      bg.fill({ color: 0x2a1040, alpha });
    }

    // Stars/particles in background
    for (let i = 0; i < 30; i++) {
      const sx = Math.random() * CANVAS_WIDTH;
      const sy = Math.random() * (GROUND_Y - 50);
      const size = Math.random() * 2 + 0.5;
      bg.circle(sx, sy, size);
      bg.fill({ color: 0xffffff, alpha: Math.random() * 0.4 + 0.1 });
    }

    // Distant mountains/volcanos
    bg.moveTo(0, GROUND_Y);
    bg.lineTo(100, GROUND_Y - 80);
    bg.lineTo(180, GROUND_Y - 40);
    bg.lineTo(250, GROUND_Y - 100);
    bg.lineTo(350, GROUND_Y - 60);
    bg.lineTo(420, GROUND_Y - 120);
    bg.lineTo(500, GROUND_Y - 50);
    bg.lineTo(580, GROUND_Y - 90);
    bg.lineTo(680, GROUND_Y - 40);
    bg.lineTo(780, GROUND_Y - 110);
    bg.lineTo(880, GROUND_Y - 60);
    bg.lineTo(950, GROUND_Y - 80);
    bg.lineTo(CANVAS_WIDTH, GROUND_Y - 30);
    bg.lineTo(CANVAS_WIDTH, GROUND_Y);
    bg.closePath();
    bg.fill({ color: 0x0d0520, alpha: 0.6 });

    // Volcano glow
    bg.circle(420, GROUND_Y - 120, 15);
    bg.fill({ color: 0xff4400, alpha: 0.15 });
    bg.circle(420, GROUND_Y - 120, 8);
    bg.fill({ color: 0xff6600, alpha: 0.2 });

    this.bgLayer.addChild(bg);
  }

  private buildGround(): void {
    const ground = new Graphics();

    // Main ground fill
    const groundColor = parseInt(COLORS.ground.replace('#', ''), 16);
    ground.rect(0, GROUND_Y, CANVAS_WIDTH, CANVAS_HEIGHT - GROUND_Y);
    ground.fill({ color: groundColor });

    // Ground surface line
    const lineColor = parseInt(COLORS.groundLine.replace('#', ''), 16);
    ground.moveTo(0, GROUND_Y);
    ground.lineTo(CANVAS_WIDTH, GROUND_Y);
    ground.stroke({ width: 2, color: lineColor });

    // Ground texture — scattered rocks and debris
    for (let i = 0; i < 20; i++) {
      const rx = Math.random() * CANVAS_WIDTH;
      const ry = GROUND_Y + 10 + Math.random() * (CANVAS_HEIGHT - GROUND_Y - 20);
      const rw = Math.random() * 15 + 5;
      const rh = Math.random() * 6 + 2;
      ground.ellipse(rx, ry, rw, rh);
      ground.fill({ color: 0x3d2800, alpha: Math.random() * 0.3 + 0.1 });
    }

    // Cracks in the ground
    for (let i = 0; i < 5; i++) {
      const cx = 100 + Math.random() * (CANVAS_WIDTH - 200);
      const cy = GROUND_Y + 5 + Math.random() * 30;
      ground.moveTo(cx, cy);
      ground.lineTo(cx + Math.random() * 40 - 20, cy + Math.random() * 20);
      ground.lineTo(cx + Math.random() * 30, cy + Math.random() * 15);
      ground.stroke({ width: 1, color: 0x1a0d00, alpha: 0.4 });
    }

    this.groundLayer.addChild(ground);
  }

  /**
   * Get or create a FighterDisplay for the given key.
   */
  getFighterDisplay(key: string, type: 'trex' | 'spinosaurus'): FighterDisplay {
    let display = this.fighterDisplays.get(key);
    if (!display) {
      // Determine head image from game config
      const config = getGameConfig();
      const headImage = type === 'trex' ? config.p1HeadImage : config.p2HeadImage;
      display = new FighterDisplay(type, headImage);
      this.fighterDisplays.set(key, display);
      this.fighterLayer.addChild(display.container);
    }
    return display;
  }

  /**
   * Clear all dynamic content (UI text, overlays).
   * Static background and ground persist.
   */
  clear(): void {
    this.hidePooledObjects();
    this.overlayLayer.removeChildren();
  }

  /**
   * Draw a fighter using the PixiJS display system.
   */
  drawFighter(
    x: number,
    y: number,
    width: number,
    height: number,
    color: string,
    facing: 'left' | 'right',
    label: string,
    state?: FighterState,
    attackTimer?: number,
    currentAttack?: AttackType | null,
    fighterKey?: string,
    fighterType?: 'trex' | 'spinosaurus',
  ): void {
    // If we have rich state info, use the PixiJS fighter display
    if (fighterKey && fighterType && state) {
      const display = this.getFighterDisplay(fighterKey, fighterType);
      const displayState: FighterDisplayState = {
        x,
        y,
        width,
        height,
        state,
        facing,
        attackTimer: attackTimer ?? 0,
        currentAttack: currentAttack ?? null,
        color,
        isHit: state === 'hit',
      };
      display.update(displayState);
      return;
    }

    // Fallback: draw a simple colored rectangle (backward compat)
    const g = this.acquireGraphics();
    const hexColor = parseInt(color.replace('#', ''), 16);
    g.rect(x - width / 2, y - height, width, height);
    g.fill({ color: hexColor });

    // Eye
    g.circle(facing === 'right' ? x + width / 4 : x - width / 4, y - height + 13, 3);
    g.fill({ color: 0x000000 });

    this.fighterLayer.addChild(g);

    if (label) {
      this.drawText(label, x, y - height - 8, {
        font: '12px monospace',
        color: '#ffffff',
      });
    }
  }

  drawHealthBar(x: number, y: number, width: number, health: number, maxHealth: number): void {
    const ratio = Math.max(0, health / maxHealth);
    const g = this.acquireGraphics();

    // Background
    const bgColor = parseInt(COLORS.healthBarBg.replace('#', ''), 16);
    g.roundRect(x, y, width, 20, 3);
    g.fill({ color: bgColor });

    // Health fill
    const barColor = ratio > 0.5 ? 0x00ff00 : ratio > 0.25 ? 0xffaa00 : 0xff0000;
    if (ratio > 0) {
      g.roundRect(x + 2, y + 2, (width - 4) * ratio, 16, 2);
      g.fill({ color: barColor });
    }

    // Border
    const borderColor = parseInt(COLORS.healthBarBorder.replace('#', ''), 16);
    g.roundRect(x, y, width, 20, 3);
    g.stroke({ width: 2, color: borderColor });

    // Health segments (tick marks)
    for (let i = 1; i < 4; i++) {
      const tickX = x + (width / 4) * i;
      g.moveTo(tickX, y);
      g.lineTo(tickX, y + 20);
      g.stroke({ width: 1, color: 0x000000, alpha: 0.3 });
    }

    this.uiLayer.addChild(g);
  }

  drawTimer(seconds: number): void {
    const text = String(Math.ceil(seconds));
    this.drawText(text, CANVAS_WIDTH / 2, 50, {
      font: 'bold 36px monospace',
      color: COLORS.text,
    });
  }

  drawText(
    content: string,
    x: number,
    y: number,
    options: { font?: string; color?: string; align?: CanvasTextAlign } = {},
  ): void {
    const font = options.font ?? '24px monospace';
    const color = options.color ?? COLORS.text;

    const style = this.acquireStyle(font, color);
    const textObj = this.acquireText();
    textObj.text = content;
    textObj.style = style;
    textObj.anchor.set(0.5, 0.5);
    textObj.x = x;
    textObj.y = y;

    this.uiLayer.addChild(textObj);
  }

  drawHitBox(hitBox: HitBox, _color = 'rgba(255, 0, 0, 0.3)'): void {
    const g = this.acquireGraphics();
    g.rect(hitBox.x, hitBox.y, hitBox.width, hitBox.height);
    g.fill({ color: 0xff0000, alpha: 0.3 });
    this.uiLayer.addChild(g);
  }

  /**
   * Apply screen-shake offset to the entire stage.
   * Call with (0, 0) to reset after shake is done.
   */
  applyScreenShake(dx: number, dy: number): void {
    this.app.stage.x = dx;
    this.app.stage.y = dy;
  }

  /**
   * Render the current frame.
   * Called once per frame after all draw calls.
   */
  render(): void {
    if (this.initialized) {
      this.app.render();
    }
  }

  /**
   * Get a 2D rendering context for backward compatibility (title scene shadow effects).
   * Falls back to an offscreen canvas since PixiJS manages its own canvas.
   */
  getContext(): CanvasRenderingContext2D {
    // Return a dummy context for backward compat; shadow effects
    // are handled differently in PixiJS via filters
    const offscreen = document.createElement('canvas');
    offscreen.width = 1;
    offscreen.height = 1;
    return offscreen.getContext('2d')!;
  }

  /**
   * Remove fighter displays whose keys start with the given prefix.
   * Call this on scene transitions to clean up stale dinosaur bodies.
   */
  removeFighterDisplays(prefix: string): void {
    for (const [key, display] of this.fighterDisplays) {
      if (key.startsWith(prefix)) {
        this.fighterLayer.removeChild(display.container);
        display.destroy();
        this.fighterDisplays.delete(key);
      }
    }
  }

  /**
   * Reset transforms on fighter displays whose keys start with the given prefix.
   * Call this on round transitions to clear stale KO animations.
   */
  resetFighterDisplays(prefix: string): void {
    for (const [key, display] of this.fighterDisplays) {
      if (key.startsWith(prefix)) {
        display.resetTransforms();
      }
    }
  }

  /**
   * Check a fighter display's KO crumble state and spawn crumble particles if needed.
   * Call this each frame during round-end / match-end phases.
   * @param fighterKey The display key (e.g. 'fight-p1')
   * @param worldX The fighter's current world X position
   * @param worldY The fighter's current world Y position
   */
  checkAndSpawnCrumble(fighterKey: string, worldX: number, worldY: number): void {
    const display = this.fighterDisplays.get(fighterKey);
    if (!display) return;

    const state = display.getKOCrumbleState();
    if (state.justStartedCrumble) {
      const particles = display.generateCrumbleParticles(worldX, worldY);
      this.hitEffects.spawnCrumble(particles);
    }
  }

  /**
   * Clean up and remove all temporary display objects for the frame.
   * Called at the start of each frame before draw calls.
   * Resets object pools instead of creating/destroying objects each frame.
   */
  beginFrame(): void {
    // Detach all pooled objects from layers (they stay in the pool for reuse)
    this.uiLayer.removeChildren();
    this.overlayLayer.removeChildren();

    // Reset pool cursors — pooled objects will be reused from index 0
    this.textPoolIndex = 0;
    this.graphicsPoolIndex = 0;

    // Hide any pooled objects that were not used last frame
    // (they are detached from layers above, so this is just bookkeeping)
  }

  // ---------- Object pool helpers ----------

  /**
   * Get a Text object from the pool, or create a new one if exhausted.
   * This prevents per-frame allocations that leak GPU texture memory on mobile.
   */
  private acquireText(): Text {
    if (this.textPoolIndex < this.textPool.length) {
      const t = this.textPool[this.textPoolIndex++];
      t.visible = true;
      return t;
    }
    const t = new Text({ text: '', style: new TextStyle({ fontFamily: 'monospace' }) });
    this.textPool.push(t);
    this.textPoolIndex++;
    return t;
  }

  /**
   * Get a Graphics object from the pool, or create a new one if exhausted.
   * The object is cleared before reuse.
   */
  private acquireGraphics(): Graphics {
    if (this.graphicsPoolIndex < this.graphicsPool.length) {
      const g = this.graphicsPool[this.graphicsPoolIndex++];
      g.clear();
      g.visible = true;
      return g;
    }
    const g = new Graphics();
    this.graphicsPool.push(g);
    this.graphicsPoolIndex++;
    return g;
  }

  /**
   * Get or create a cached TextStyle for the given font string and color.
   */
  private acquireStyle(font: string, color: string): TextStyle {
    const key = `${font}|${color}`;
    let style = this.styleCache.get(key);
    if (style) return style;

    const fontParts = font.match(/(bold\s+)?(\d+)px\s+(\w+)/);
    const fontSize = fontParts ? parseInt(fontParts[2]) : 24;
    const fontWeight = fontParts?.[1]?.trim() === 'bold' ? 'bold' : 'normal';

    style = new TextStyle({
      fontFamily: 'monospace',
      fontSize,
      fontWeight: fontWeight as 'bold' | 'normal',
      fill: color,
    });
    this.styleCache.set(key, style);
    return style;
  }

  /**
   * Hide all pooled objects (used by clear()).
   */
  private hidePooledObjects(): void {
    for (const t of this.textPool) {
      t.visible = false;
    }
    for (const g of this.graphicsPool) {
      g.visible = false;
    }
  }

  /**
   * Destroy the renderer and all PixiJS resources.
   */
  destroy(): void {
    for (const display of this.fighterDisplays.values()) {
      display.destroy();
    }
    this.fighterDisplays.clear();

    this.hitEffects.destroy();

    // Destroy pooled objects to free GPU resources
    for (const t of this.textPool) {
      t.destroy(true);
    }
    this.textPool.length = 0;
    for (const g of this.graphicsPool) {
      g.destroy(true);
    }
    this.graphicsPool.length = 0;
    this.styleCache.clear();

    this.app.destroy(true);
    logger.info('Renderer destroyed');
  }
}
